





import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Card, CardContent, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Box, Grid, IconButton, Chip, InputAdornment, Fade, Slide,
  makeStyles, createTheme, ThemeProvider, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  Divider, Tooltip
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Code as CodeIcon,
  Clear as ClearIcon,
  LocalShipping as LocalShippingIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Cached as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@material-ui/icons';
import Navbar from '../../components/Navbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const API = 'https://backend.csibon.app';

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2', light: '#42a5f5', dark: '#1565c0' },
    secondary: { main: '#dc004e', light: '#ff5983', dark: '#9a0036' },
    background: { default: '#f5f7fa', paper: '#ffffff' },
    success: { main: '#2e7d32', light: '#4caf50', dark: '#1b5e20' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700, color: '#1e293b' },
    h5: { fontWeight: 600, color: '#334155' },
    h6: { fontWeight: 600, color: '#475569' },
    body1: { color: '#64748b' },
  },
  shape: { borderRadius: 12 },
});

const useStyles = makeStyles((theme) => ({
  root: { minHeight: '100vh', backgroundColor: theme.palette.background.default, paddingBottom: theme.spacing(4) },
  header: {
    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    color: 'white', padding: theme.spacing(6, 0), marginBottom: theme.spacing(4),
    boxShadow: '0 10px 40px rgba(25, 118, 210, 0.2)', position: 'relative', overflow: 'hidden',
    '&::before': {
      content: '""', position: 'absolute', top: 0, right: 0, width: '40%', height: '100%',
      background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.1\'%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'4\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat',
    },
  },
  headerContent: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 },
  headerTitle: { display: 'flex', alignItems: 'center', gap: theme.spacing(2) },
  headerIcon: { fontSize: '3rem', opacity: 0.9 },

  formCard: {
    marginBottom: theme.spacing(4), borderRadius: theme.shape.borderRadius, boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
    border: '1px solid rgba(255,255,255,0.2)', overflow: 'hidden',
  },
  formHeader: { background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', padding: theme.spacing(3), borderBottom: '1px solid #e2e8f0' },
  formContent: { padding: theme.spacing(4) },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.shape.borderRadius, transition: 'all 0.3s ease',
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.light },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main, borderWidth: 2 },
    },
    '& .MuiInputLabel-root': { fontWeight: 500 },
  },
  submitButton: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: 'white', padding: theme.spacing(1.5, 4), borderRadius: theme.shape.borderRadius, fontWeight: 600, fontSize: '1rem', textTransform: 'none',
    boxShadow: '0 8px 25px rgba(25,118,210,0.3)', transition: 'all 0.3s ease',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 35px rgba(25,118,210,0.4)' },
  },

  tableCard: { borderRadius: theme.shape.borderRadius, boxShadow: '0 8px 32px rgba(0,0,0,0.1)', border: '1px solid rgba(255,255,255,0.2)', overflow: 'hidden', marginBottom: theme.spacing(3) },
  tableHeader: {
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    padding: theme.spacing(3), borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: theme.spacing(2),
  },
  searchField: { minWidth: 300, '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: theme.shape.borderRadius } },
  tableContainer: { maxHeight: 600 },
  tableRow: { transition: 'all 0.3s ease', '&:hover': { backgroundColor: '#f8fafc', transform: 'translateX(4px)' } },
  tableCell: { borderBottom: '1px solid #e2e8f0', padding: theme.spacing(2) },
  editableCell: {
    '& .MuiTextField-root .MuiOutlinedInput-root': {
      backgroundColor: '#f8f9fa', borderRadius: 8, '&:hover': { backgroundColor: 'white' }, '&.Mui-focused': { backgroundColor: 'white' },
    },
  },
  actionButton: { margin: theme.spacing(0, 0.5), borderRadius: 8, transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-1px)' } },
  saveButton: { backgroundColor: theme.palette.success.main, color: 'white', '&:hover': { backgroundColor: theme.palette.success.dark } },
  deleteButton: { backgroundColor: theme.palette.secondary.main, color: 'white', '&:hover': { backgroundColor: theme.palette.secondary.dark } },

  statsContainer: { marginBottom: theme.spacing(3) },
  statCard: {
    padding: theme.spacing(2), textAlign: 'center', background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    border: '1px solid #e2e8f0', borderRadius: theme.shape.borderRadius, transition: 'all 0.3s ease',
    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(0,0,0,0.1)' },
  },
  statNumber: { fontSize: '2rem', fontWeight: 700, color: theme.palette.primary.main, marginBottom: theme.spacing(0.5) },
  statLabel: { color: '#64748b', fontWeight: 500, fontSize: '0.9rem' },
  emptyState: { textAlign: 'center', padding: theme.spacing(6), color: '#64748b' },
  emptyIcon: { fontSize: '4rem', marginBottom: theme.spacing(2), opacity: 0.5 },

  // Livraison panel
  deliveryPanel: { marginTop: theme.spacing(2), padding: theme.spacing(2), border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff' },
  panelHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  subCard: { padding: theme.spacing(2), border: '1px solid #e2e8f0', borderRadius: 12, background: '#fafbfc' },
  smallInput: { marginBottom: theme.spacing(1) },
  inlineActions: { display: 'flex', alignItems: 'center', gap: 8 },
  tagActive: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 8px', borderRadius: 999, background: '#e6f4ea', color: '#1e7e34', fontSize: 12, fontWeight: 600 },
  tagInactive: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 8px', borderRadius: 999, background: '#fdecea', color: '#b71c1c', fontSize: 12, fontWeight: 600 },
}));

function AddEntreprisePage() {
  const classes = useStyles();

  // Entreprises
  const [nomEntreprise, setNomEntreprise] = useState('');
  const [codeEntreprise, setCodeEntreprise] = useState('');
  const [adresse, setAdresse] = useState('');
  const [entreprises, setEntreprises] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [modifications, setModifications] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEntreprise, setSelectedEntreprise] = useState(null);
  const [loading, setLoading] = useState(false);

  // Livraison
  const [expandedEntrepriseId, setExpandedEntrepriseId] = useState(null);

  // Codes livraison
  const [deliveryCodes, setDeliveryCodes] = useState([]);
  const [codesLoading, setCodesLoading] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [savingCode, setSavingCode] = useState(false);

  // Établissements liés
  const [linkedEstabs, setLinkedEstabs] = useState([]);
  const [estabsLoading, setEstabsLoading] = useState(false);
  const [deliveryNameInput, setDeliveryNameInput] = useState('');
  const [deliveryNameEdits, setDeliveryNameEdits] = useState({});

  // Autocomplete établissements (recherche serveur)
  const [estabQuery, setEstabQuery] = useState('');
  const [estabOptions, setEstabOptions] = useState([]);
  const [estabLoading, setEstabLoading] = useState(false);
  const [selectedEstab, setSelectedEstab] = useState(null); // {id,name,city}

  const fetchEntreprises = async () => {
    try {
      const response = await fetch(`${API}/entreprise/all`);
      if (response.ok) {
        const data = await response.json();
        setEntreprises(data);
      } else {
        toast.error("Erreur lors du chargement des entreprises");
      }
    } catch (e) {
      console.error(e);
      toast.error("Erreur de connexion");
    }
  };

  useEffect(() => { fetchEntreprises(); }, []);

  const filteredEntreprises = entreprises.filter(e =>
    !searchTerm ? true : (e.nom_entreprise || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // CRUD Entreprise
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nomEntreprise.trim() || !codeEntreprise.trim() || !adresse.trim()) {
      toast.error('Veuillez remplir tous les champs'); return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/entreprise/add`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nom_entreprise: nomEntreprise, code_entreprise: codeEntreprise, adresse })
      });
      if (res.ok) {
        const j = await res.json();
        setEntreprises(prev => [...prev, j.entreprise]);
        setNomEntreprise(''); setCodeEntreprise(''); setAdresse('');
        toast.success('Entreprise ajoutée');
      } else toast.error("Erreur lors de l'ajout");
    } catch (e) { console.error(e); toast.error("Erreur lors de l'envoi"); }
    finally { setLoading(false); }
  };

  const handleInputChange = (id, field, value) => {
    setModifications(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const handleSave = async (id) => {
    const modified = modifications[id];
    if (!modified) { toast.info('Aucune modification'); return; }
    try {
      const res = await fetch(`${API}/entreprise/update/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(modified)
      });
      if (res.ok) {
        setEntreprises(prev => prev.map(e => e.id === id ? ({ ...e, ...modified }) : e));
        setModifications(prev => { const n = { ...prev }; delete n[id]; return n; });
        toast.success('Entreprise mise à jour');
      } else toast.error('Erreur mise à jour');
    } catch (e) { console.error(e); toast.error('Erreur lors de la MAJ'); }
  };

  const handleDeleteClick = (entreprise) => { setSelectedEntreprise(entreprise); setDeleteDialogOpen(true); };

  const handleDelete = async () => {
    if (!selectedEntreprise) return;
    try {
      const res = await fetch(`${API}/entreprise/delete/${selectedEntreprise.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ suppr: true })
      });
      if (res.ok) {
        setEntreprises(prev => prev.filter(e => e.id !== selectedEntreprise.id));
        toast.success('Entreprise supprimée');
      } else toast.error('Erreur suppression');
    } catch (e) { console.error(e); toast.error('Erreur lors de la suppression'); }
    finally { setDeleteDialogOpen(false); setSelectedEntreprise(null); }
  };

  // ---------- Livraison : ouverture panneau ----------
  const openDeliveryPanel = async (entreprise) => {
    const id = entreprise.id;
    setExpandedEntrepriseId(prev => prev === id ? null : id);
    if (expandedEntrepriseId !== id) {
      await Promise.all([fetchDeliveryCodes(id), fetchLinkedEstablishments(id)]);
      setEstabQuery('');
      setEstabOptions([]);
      setSelectedEstab(null);
      setDeliveryNameInput('');
      setDeliveryNameEdits({});
    }
  };

  // ---------- Codes livraison ----------
  const fetchDeliveryCodes = async (entrepriseId) => {
    setCodesLoading(true);
    try {
      const res = await fetch(`${API}/entreprise/delivery-code/list?entreprise_id=${entrepriseId}`);
      if (res.ok) {
        const j = await res.json();
        setDeliveryCodes(j || []);
      } else {
        setDeliveryCodes([]);
        toast.error('Erreur chargement codes livraison');
      }
    } catch (e) {
      console.error(e);
      toast.error('Erreur de connexion (codes)');
    } finally { setCodesLoading(false); }
  };

  const addDeliveryCode = async () => {
    if (!newCode.trim()) { toast.error('Code requis'); return; }
    setSavingCode(true);
    try {
      const res = await fetch(`${API}/entreprise/delivery-code/add`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entreprise_id: expandedEntrepriseId,
          code: newCode.trim().toUpperCase(),
          active: true
        })
      });
      if (res.ok) {
        toast.success('Code ajouté');
        setNewCode('');
        fetchDeliveryCodes(expandedEntrepriseId);
      } else toast.error('Impossible d’ajouter le code');
    } catch (e) { console.error(e); toast.error('Erreur réseau (ajout code)'); }
    finally { setSavingCode(false); }
  };

  const toggleDeliveryCode = async (code) => {
    try {
      const res = await fetch(`${API}/entreprise/delivery-code/update/${code.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !code.active })
      });
      if (res.ok) {
        toast.success(code.active ? 'Code désactivé' : 'Code activé');
        fetchDeliveryCodes(expandedEntrepriseId);
      } else toast.error('Action impossible');
    } catch (e) { console.error(e); toast.error('Erreur réseau'); }
  };

  const deleteDeliveryCode = async (code) => {
    if (!window.confirm(`Supprimer le code "${code.code}" ?`)) return;
    try {
      const res = await fetch(`${API}/entreprise/delivery-code/delete/${code.id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Code supprimé');
        setDeliveryCodes(prev => prev.filter(c => c.id !== code.id));
      } else toast.error('Suppression impossible');
    } catch (e) { console.error(e); toast.error('Erreur réseau'); }
  };

  // ---------- Établissements liés ----------
  const fetchLinkedEstablishments = async (entrepriseId) => {
    setEstabsLoading(true);
    try {
      const res = await fetch(`${API}/entreprise/${entrepriseId}/establishments`);
      if (res.ok) {
        const j = await res.json();
        setLinkedEstabs(j || []);
      } else {
        setLinkedEstabs([]);
        toast.error('Erreur chargement établissements liés');
      }
    } catch (e) {
      console.error(e);
      toast.error('Erreur de connexion (établissements)');
    } finally { setEstabsLoading(false); }
  };

  // ---------- Autocomplete établissements (serveur) ----------
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const run = async () => {
      const q = estabQuery.trim();
      if (q.length < 2 || !expandedEntrepriseId) { setEstabOptions([]); return; }
      setEstabLoading(true);
      try {
        const res = await fetch(`${API}/entreprise/etablissement/search?q=${encodeURIComponent(q)}&limit=10`, { signal: controller.signal });
        if (res.ok) {
          const data = await res.json(); // [{id,name,city}]
          if (!cancelled) setEstabOptions(Array.isArray(data) ? data : []);
        } else {
          if (!cancelled) setEstabOptions([]);
        }
      } catch (e) {
        if (!cancelled && e.name !== 'AbortError') console.error(e);
      } finally {
        if (!cancelled) setEstabLoading(false);
      }
    };

    const t = setTimeout(run, 250); // debounce
    return () => { clearTimeout(t); cancelled = true; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estabQuery, expandedEntrepriseId]);

  const linkSelectedEstablishment = async () => {
    if (!selectedEstab?.id) { toast.error("Sélectionnez un établissement"); return; }
    try {
      const res = await fetch(`${API}/entreprise/etablissement/link`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entreprise_id: expandedEntrepriseId,
          etablissement_id: Number(selectedEstab.id),
          delivery_name: deliveryNameInput.trim(),
        })
      });
      if (res.ok) {
        toast.success('Établissement lié');
        setSelectedEstab(null);
        setEstabQuery('');
        setEstabOptions([]);
        setDeliveryNameInput('');
        fetchLinkedEstablishments(expandedEntrepriseId);
      } else toast.error('Lien impossible');
    } catch (e) { console.error(e); toast.error('Erreur réseau (lien)'); }
  };

  const unlinkEstablishment = async (etabId) => {
    if (!window.confirm(`Désactiver le lien avec l’établissement #${etabId} ?`)) return;
    try {
      const res = await fetch(`${API}/entreprise/etablissement/unlink`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entreprise_id: expandedEntrepriseId, etablissement_id: etabId })
      });
      if (res.ok) {
        toast.success('Lien désactivé');
        setLinkedEstabs(prev => prev.filter(x => x.id !== etabId));
        setDeliveryNameEdits(prev => {
          const next = { ...prev };
          delete next[etabId];
          return next;
        });
      } else toast.error('Action impossible');
    } catch (e) { console.error(e); toast.error('Erreur réseau'); }
  };

  const handleDeliveryNameChange = (etabId, value) => {
    setDeliveryNameEdits(prev => ({ ...prev, [etabId]: value }));
  };

  const saveDeliveryName = async (etabId) => {
    if (!expandedEntrepriseId) return;
    if (!(etabId in deliveryNameEdits)) {
      toast.info('Aucune modification');
      return;
    }
    const nextName = (deliveryNameEdits[etabId] || '').trim();
    try {
      const res = await fetch(`${API}/entreprise/etablissement/update`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entreprise_id: expandedEntrepriseId,
          etablissement_id: etabId,
          delivery_name: nextName,
        }),
      });
      if (res.ok) {
        toast.success('Nom livraison mis à jour');
        setLinkedEstabs(prev => prev.map(et => (
          et.id === etabId ? { ...et, delivery_name: nextName || null } : et
        )));
        setDeliveryNameEdits(prev => {
          const next = { ...prev };
          delete next[etabId];
          return next;
        });
      } else {
        toast.error('Impossible de mettre à jour le nom');
      }
    } catch (e) {
      console.error(e);
      toast.error('Erreur réseau (nom livraison)');
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.root}>
        <Navbar />
        <ToastContainer position="top-right" autoClose={3000} />

        {/* Header */}
        <div className={classes.header}>
          <Container>
            <div className={classes.headerContent}>
              <div className={classes.headerTitle}>
                <BusinessIcon className={classes.headerIcon} />
                <div>
                  <Typography variant="h4">Gestion des Entreprises</Typography>
                  <Typography variant="body1" style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.1rem' }}>
                    Ajoutez, modifiez… et pilotez la <b>livraison</b> (codes / établissements)
                  </Typography>
                </div>
              </div>
            </div>
          </Container>
        </div>

        <Container>
          {/* Stats */}
          <Fade in timeout={1000}>
            <Grid container spacing={3} className={classes.statsContainer}>
              <Grid item xs={12} sm={4}>
                <Card className={classes.statCard}><CardContent>
                  <div className={classes.statNumber}>{entreprises.length}</div>
                  <div className={classes.statLabel}>Total des entreprises</div>
                </CardContent></Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card className={classes.statCard}><CardContent>
                  <div className={classes.statNumber}>{filteredEntreprises.length}</div>
                  <div className={classes.statLabel}>Entreprises affichées</div>
                </CardContent></Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card className={classes.statCard}><CardContent>
                  <div className={classes.statNumber}>{Object.keys(modifications).length}</div>
                  <div className={classes.statLabel}>Modifications en attente</div>
                </CardContent></Card>
              </Grid>
            </Grid>
          </Fade>

          {/* Formulaire d'ajout */}
          <Slide direction="up" in timeout={800}>
            <Card className={classes.formCard}>
              <div className={classes.formHeader}>
                <Box display="flex" alignItems="center" gap={2}>
                  <AddIcon color="primary" />
                  <Typography variant="h6">Ajouter une nouvelle entreprise</Typography>
                </Box>
              </div>
              <div className={classes.formContent}>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Nom de l'entreprise" variant="outlined" value={nomEntreprise}
                        onChange={(e) => setNomEntreprise(e.target.value)} className={classes.textField}
                        InputProps={{ startAdornment: <InputAdornment position="start"><BusinessIcon color="action" /></InputAdornment> }}
                        required />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Code de l'entreprise" variant="outlined" value={codeEntreprise}
                        onChange={(e) => setCodeEntreprise(e.target.value)} className={classes.textField}
                        InputProps={{ startAdornment: <InputAdornment position="start"><CodeIcon color="action" /></InputAdornment> }}
                        required />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Adresse" variant="outlined" value={adresse}
                        onChange={(e) => setAdresse(e.target.value)} className={classes.textField}
                        required />
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="center">
                        <Button type="submit" className={classes.submitButton} startIcon={<AddIcon />} disabled={loading} size="large">
                          {loading ? 'Ajout en cours...' : 'Ajouter l\'entreprise'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              </div>
            </Card>
          </Slide>

          {/* Liste des entreprises */}
          <Slide direction="up" in timeout={1200}>
            <Card className={classes.tableCard}>
              <div className={classes.tableHeader}>
                <Typography variant="h6" color="primary">Liste des entreprises ({filteredEntreprises.length})</Typography>
                <TextField className={classes.searchField} variant="outlined" placeholder="Rechercher par nom d'entreprise"
                  value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
                    endAdornment: searchTerm && <InputAdornment position="end"><IconButton onClick={() => setSearchTerm('')} size="small"><ClearIcon /></IconButton></InputAdornment>
                  }} />
              </div>

              {filteredEntreprises.length === 0 ? (
                <div className={classes.emptyState}>
                  <BusinessIcon className={classes.emptyIcon} />
                  <Typography variant="h6" gutterBottom>{searchTerm ? 'Aucune entreprise trouvée' : 'Aucune entreprise enregistrée'}</Typography>
                  <Typography variant="body2">{searchTerm ? 'Essayez avec d\'autres termes' : 'Ajoutez votre première entreprise'}</Typography>
                </div>
              ) : (
                <TableContainer className={classes.tableContainer}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.tableCell}><Typography variant="subtitle2" color="primary">Nom</Typography></TableCell>
                        <TableCell className={classes.tableCell}><Typography variant="subtitle2" color="primary">Code</Typography></TableCell>
                        <TableCell className={classes.tableCell}><Typography variant="subtitle2" color="primary">Adresse</Typography></TableCell>
                        <TableCell className={classes.tableCell}><Typography variant="subtitle2" color="primary">Actions</Typography></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredEntreprises.map((entreprise) => {
                        const modif = modifications[entreprise.id] || entreprise;
                        const hasModifications = Boolean(modifications[entreprise.id]);
                        const expanded = expandedEntrepriseId === entreprise.id;

                        return (
                          <React.Fragment key={entreprise.id}>
                            <TableRow className={classes.tableRow}>
                              <TableCell className={`${classes.tableCell} ${classes.editableCell}`}>
                                <TextField fullWidth variant="outlined" value={modif.nom_entreprise || ''}
                                  onChange={(e) => handleInputChange(entreprise.id, 'nom_entreprise', e.target.value)} size="small" />
                              </TableCell>
                              <TableCell className={`${classes.tableCell} ${classes.editableCell}`}>
                                <TextField fullWidth variant="outlined" value={modif.code_entreprise || ''}
                                  onChange={(e) => handleInputChange(entreprise.id, 'code_entreprise', e.target.value)} size="small" />
                              </TableCell>
                              <TableCell className={`${classes.tableCell} ${classes.editableCell}`}>
                                <TextField fullWidth variant="outlined" value={modif.adresse || ''}
                                  onChange={(e) => handleInputChange(entreprise.id, 'adresse', e.target.value)} size="small" />
                              </TableCell>
                              <TableCell className={classes.tableCell}>
                                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                  {hasModifications && <Chip label="Modifié" size="small" color="secondary" variant="outlined" />}
                                  <Tooltip title="Sauvegarder"><IconButton className={`${classes.actionButton} ${classes.saveButton}`} onClick={() => handleSave(entreprise.id)} size="small"><SaveIcon /></IconButton></Tooltip>
                                  <Tooltip title="Supprimer"><IconButton className={`${classes.actionButton} ${classes.deleteButton}`} onClick={() => handleDeleteClick(entreprise)} size="small"><DeleteIcon /></IconButton></Tooltip>
                                  <Tooltip title="Gérer la livraison"><IconButton className={classes.actionButton} onClick={() => openDeliveryPanel(entreprise)} size="small"><LocalShippingIcon /></IconButton></Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>

                            {/* PANNEAU LIVRAISON */}
                            {expanded && (
                              <TableRow>
                                <TableCell colSpan={4} style={{ background: '#fcfcfe' }}>
                                  <div className={classes.deliveryPanel}>
                                    <div className={classes.panelHeader}>
                                      <LocalShippingIcon color="primary" />
                                      <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                                        Livraison — {entreprise.nom_entreprise} (ID: {entreprise.id})
                                      </Typography>
                                      <Box flex={1} />
                                      <IconButton onClick={() => { fetchDeliveryCodes(entreprise.id); fetchLinkedEstablishments(entreprise.id); }}>
                                        <RefreshIcon />
                                      </IconButton>
                                    </div>
                                    <Divider style={{ margin: '8px 0 16px' }} />

                                    <Grid container spacing={2}>
                                      {/* Codes livraison */}
                                      <Grid item xs={12} md={5}>
                                        <Card className={classes.subCard}>
                                          <Box display="flex" alignItems="center" style={{ gap: 8 }} mb={1}>
                                            <CodeIcon color="action" />
                                            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>Codes livraison</Typography>
                                          </Box>

                                          <Box display="grid" style={{ gap: 8 }} mb={2}>
                                            <TextField
                                              label="Nouveau code"
                                              placeholder="ACME-2025"
                                              value={newCode}
                                              onChange={(e) => setNewCode(e.target.value)}
                                              variant="outlined"
                                              size="small"
                                              className={classes.smallInput}
                                            />
                                            <Button onClick={addDeliveryCode} variant="contained" color="primary" disabled={savingCode}>
                                              {savingCode ? 'Ajout…' : 'Ajouter le code'}
                                            </Button>
                                          </Box>

                                          <Divider style={{ margin: '8px 0' }} />
                                          <Box display="flex" alignItems="center" style={{ gap: 6 }} mb={1}>
                                            <Typography variant="body2" style={{ fontWeight: 600 }}>Codes existants</Typography>
                                            <IconButton size="small" onClick={() => fetchDeliveryCodes(entreprise.id)}><RefreshIcon fontSize="small" /></IconButton>
                                          </Box>

                                          {codesLoading ? (
                                            <Typography variant="body2">Chargement…</Typography>
                                          ) : deliveryCodes.length === 0 ? (
                                            <Typography variant="body2" color="textSecondary">Aucun code</Typography>
                                          ) : (
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow>
                                                  <TableCell>Code</TableCell>
                                                  <TableCell>Statut</TableCell>
                                                  <TableCell align="right">Actions</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {deliveryCodes.map(c => (
                                                  <TableRow key={c.id}>
                                                    <TableCell>{c.code}</TableCell>
                                                    <TableCell>
                                                      {c.active ? (
                                                        <span className={classes.tagActive}><CheckCircleIcon style={{ fontSize: 14 }} /> Actif</span>
                                                      ) : (
                                                        <span className={classes.tagInactive}><CancelIcon style={{ fontSize: 14 }} /> Inactif</span>
                                                      )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      <Box className={classes.inlineActions}>
                                                        <Button size="small" onClick={() => toggleDeliveryCode(c)}>
                                                          {c.active ? 'Désactiver' : 'Activer'}
                                                        </Button>
                                                        <IconButton size="small" onClick={() => deleteDeliveryCode(c)}><DeleteIcon fontSize="small" /></IconButton>
                                                      </Box>
                                                    </TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          )}
                                        </Card>
                                      </Grid>

                                      {/* Liaisons établissements */}
                                      <Grid item xs={12} md={7}>
                                        <Card className={classes.subCard}>
                                          <Box display="flex" alignItems="center" style={{ gap: 8 }} mb={1}>
                                            <LinkIcon color="action" />
                                            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>Établissements liés</Typography>
                                          </Box>

                                          {/* Autocomplete établissement */}
                                          <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                              <Autocomplete
                                                options={estabOptions}
                                                getOptionSelected={(o, v) => o.id === v.id}
                                                getOptionLabel={(o) => o?.name ? `${o.name}${o.city ? ' — ' + o.city : ''}` : ''}
                                                loading={estabLoading}
                                                inputValue={estabQuery}
                                                value={selectedEstab}
                                                onInputChange={(e, value) => setEstabQuery(value || '')}
                                                onChange={(e, value) => setSelectedEstab(value || null)}
                                                renderInput={(params) => (
                                                  <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    size="small"
                                                    label="Rechercher un établissement (tapez ≥ 2 caractères)"
                                                  />
                                                )}
                                                noOptionsText={estabQuery.length < 2 ? 'Tapez 2+ caractères' : 'Aucun résultat'}
                                              />
                                            </Grid>
                                            <Grid item xs={12}>
                                              <TextField
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                label="Nom affiché (livraison)"
                                                placeholder="Ex: Siège Paris - Livraison"
                                                value={deliveryNameInput}
                                                onChange={(e) => setDeliveryNameInput(e.target.value)}
                                              />
                                            </Grid>

                                            <Grid item xs={12}>
                                              <Box display="flex" style={{ gap: 8 }}>
                                                <Button variant="contained" color="primary" onClick={linkSelectedEstablishment} disabled={!selectedEstab}>
                                                  Lier l’établissement sélectionné
                                                </Button>
                                                <Button variant="outlined" onClick={() => fetchLinkedEstablishments(expandedEntrepriseId)}>
                                                  Rafraîchir la liste liée
                                                </Button>
                                              </Box>
                                            </Grid>
                                          </Grid>

                                          <Divider style={{ margin: '12px 0' }} />
                                          {estabsLoading ? (
                                            <Typography variant="body2">Chargement…</Typography>
                                          ) : linkedEstabs.length === 0 ? (
                                            <Typography variant="body2" color="textSecondary">Aucun établissement lié.</Typography>
                                          ) : (
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow>
                                                  <TableCell>ID</TableCell>
                                                  <TableCell>Nom</TableCell>
                                                  <TableCell>Nom livraison</TableCell>
                                                  <TableCell>Ville</TableCell>
                                                  <TableCell align="right">Actions</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {linkedEstabs.map(et => (
                                                  <TableRow key={et.id}>
                                                    <TableCell>{et.id}</TableCell>
                                                    <TableCell>{et.name}</TableCell>
                                                    <TableCell>
                                                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                                        <TextField
                                                          value={deliveryNameEdits[et.id] ?? et.delivery_name ?? ''}
                                                          onChange={(e) => handleDeliveryNameChange(et.id, e.target.value)}
                                                          variant="outlined"
                                                          size="small"
                                                          placeholder="Nom spécifique"
                                                        />
                                                        <IconButton size="small" title="Sauvegarder le nom" onClick={() => saveDeliveryName(et.id)}>
                                                          <SaveIcon fontSize="small" />
                                                        </IconButton>
                                                      </Box>
                                                    </TableCell>
                                                    <TableCell>{et.city || '—'}</TableCell>
                                                    <TableCell align="right">
                                                      <IconButton size="small" title="Délier" onClick={() => unlinkEstablishment(et.id)}>
                                                        <LinkOffIcon fontSize="small" />
                                                      </IconButton>
                                                    </TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          )}
                                        </Card>
                                      </Grid>
                                    </Grid>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          </Slide>
        </Container>

        {/* Dialog suppression */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <DeleteIcon color="error" /> Confirmer la suppression
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Supprimer <strong>{selectedEntreprise?.nom_entreprise}</strong> ? Cette action est irréversible.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} color="primary">Annuler</Button>
            <Button onClick={handleDelete} color="secondary" variant="contained">Supprimer</Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  );
}

export default AddEntreprisePage;